/* eslint-disable prefer-const */
import DateComponent from "@/components/shared/DateComponent";
import style from "@/styles/pages/Profile/PersonalInfo.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import InputMask from "react-input-mask";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setDateOfBirthYear,
  setDateOfBirthMonth,
  setDateOfBirthDay,
  setFirstName,
  setLastName,
  setMiddleName,
  setPhoneNumber,
  setPhoneType,
  setTitle,
  setSin,
  setMaritalStatus,
  setNumOfDependents,
  setCitizenshipStatus,
  setFirstTimeBuyer,
  setRecentResident,
  setEmailAddress,
  setApplicantId,
  clearApplicantState,
} from "@/slices/applicantSlice";
import { RootState } from "@/store/dtm";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isValidSin, setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors, validateAge } from "@/lib/util";
import ToolTip from "@/components/shared/ToolTip";
import { setMortgageComplete } from "@/slices/progressSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { applicationCategory } from "@/types/applicationType";
import { createApiClient } from "@/lib/Requests";
import { AddressDto, ApplicantForRegistrationDto, ApplicationDto, CreateApplicantModel, CreateDealCommand, CreateEmploymentModel, EmploymentDto, EmploymentForManipulationDto, EmploymentIncomeDto, MortgageApplicationForCreateDto, OtherIncome, ReferralProductDto, SearchApplicantModel, TokenData, UpdateDealCommand, UpdateEmploymentModel } from "@/lib/DTMWebAPIClient.generated";
import { clearApplicationState, setApplicationId, setApplicationProgress } from "@/slices/applicationSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setEmploymentId } from "@/slices/employmentSlice";
import { useAuth } from "@/context/AuthContext";
import { selectHomeAddress } from "@/slices/homeAddressSlice";
import { selectPreviousAddress } from "@/slices/PreviousAddressSlice";
import { addAsset, assetInfo } from "@/slices/assetSlice";

export default function PersonalInfo() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("no");
  const [dobYear, setDobYear] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [ageErrorState, setAgeErrorState] = useState({ hasError: false, errorMessage: "", invalidYear: false, invalidDay: false })
  const [sinShown, setSinShown] = useState(false);
  const [firstTimeHomeBuyer, setFirstTimeHomeBuyer] = useState("");
  const [newToCanada, setNewToCanada] = useState("");
  const [incomeAmount, setIncomeAmount] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [displayExistingMemberError, setDisplayExistingMemberError] = useState(false);

  const dispatch = useDispatch();
  const { login } = useAuth();
  dayjs.extend(customParseFormat);

  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const employment = useSelector(
    (state: RootState) => state.employment.employment
  );
  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
  const homeAddress = useSelector(selectHomeAddress).address;
  const previousAddresses = useSelector(selectPreviousAddress).addresses;
  const propertyInfo = useSelector(
    (state: RootState) => state.propertyInfo.propertyInfo
  );
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const methods = useForm({
    defaultValues: {
      title: "",
      firstName: applicant.firstName,
      middleName: applicant.middleName,
      lastName: applicant.lastName,
      sin: applicant.sin,
      email: applicant.email,
      phoneNumber: applicant.phoneNumber,
      phoneType: applicant.phoneType,
      maritalStatus: "",
      numOfDependents: "",
      citizenshipStatus: "",
      firstTimeBuyer: "",
      newToCanada: "",
      year: applicant.dob_year,
      month: applicant.dob_month,
      day: applicant.dob_day,
      employmentStatus: "",
      employmentType: "",
      payrollType: "",
      income: "",
      payrollFrequency: ""
    }
  });

  const validateDay = () => {
    if (ageErrorState.invalidDay) {
      return ageErrorState.errorMessage;
    } else {
      return true;
    }
  }

  const validateYear = () => {
    if (ageErrorState.invalidYear) {
      return ageErrorState.errorMessage;
    } else {
      return true;
    }
  }

  useEffect(() => {
    let dob = dobYear + "/" + dobMonth + "/" + dobDay; //Date of birth in yyyy/mm/dd format
    let validationErrors = validateAge(dob);

    if (!validationErrors.hasError) {
      methods.clearErrors('year')
      methods.clearErrors('month')
      methods.clearErrors('day')
    }

    setAgeErrorState(validationErrors)
    validateDay();
    validateYear();
  }, [dobYear, dobMonth, dobDay]);

  useEffect(() => {
    document.title = "Tell us about yourself";

    setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
    dispatch(setMortgageComplete(true));
    methods.setValue("title", applicant.title);
    methods.setValue("phoneType", applicant.phoneType);
    methods.setValue("maritalStatus", applicant.maritalStatus);
    methods.setValue("numOfDependents", applicant.numOfDependents.toString());
    methods.setValue("citizenshipStatus", applicant.citizenshipStatus);
    methods.setValue("firstTimeBuyer", applicant.firstTimeBuyer);
    setFirstTimeHomeBuyer(applicant.firstTimeBuyer);
    methods.setValue("newToCanada", applicant.recentResident);
    setNewToCanada(applicant.recentResident);
    methods.setValue("employmentStatus", employment.status);
    methods.setValue("employmentType", employment.type);
    methods.setValue("payrollType", employment.payrollType);
    let formattedIncome = employment.income.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    methods.setValue("income", formattedIncome);
    setIncomeAmount(employment.income == 0 ? "" : employment.income.toString());
    methods.setValue("payrollFrequency", employment.payrollFrequency);

    setDobDay(methods.getValues('day'));
    setDobMonth(methods.getValues('month'));
    setDobYear(methods.getValues('year'));
    setSelected(applicant.hasJointApp ? "yes" : "no");

    if (applicant.isProspect) {
      methods.setValue("email", credential.credential.email);
    }

    //Refresh Token if it is expired
    if (credential.credential.expiration && dayjs(credential.credential.expiration).diff(dayjs(), 'minute') < 1) {
      client.apiAuthRefreshtoken(new TokenData({
        accessToken: credential.credential.accessToken,
        refreshToken: credential.credential.refreshToken
      })).then((result) => {
        login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");
        updateAppData();
      })
    } else {
      updateAppData();
    }

    window.onpopstate = (e: any) => {
      console.log("browser back detected");
    }
  }, []);

  const updateAppData = () => {
    client.apiApplicationsFetch().then((result) => {
      result.application = application;
      result.propertyInfo = propertyInfo;
      result.application = application;
      result.referralProducts = referralProducts;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });
  }

  const setYearHandler = (e: any) => {
    setDobYear(e.target.value)
  };
  const setMonthHandler = (e: any) => {
    setDobMonth(e.target.value)
  };
  const setDayHandler = (e: any) => {
    setDobDay(e.target.value)
  };

  const firstTimeBuyerChangeHandler = (e: any) => {
    setFirstTimeHomeBuyer(e.target.value);
    methods.clearErrors("firstTimeBuyer");
    methods.setValue("firstTimeBuyer", e.target.value);
  };
  const recentResidentChangeHandler = (e: any) => {
    setNewToCanada(e.target.value);
    methods.clearErrors("newToCanada");
    methods.setValue("newToCanada", e.target.value);
  };
  const onError = (errors: any) => {
    if (errors.phoneNumber != undefined) {
      window.scroll(0, window.innerHeight / 2);
    }
    setClickInsightFormErrors(errors);
  }

  const toggleSinVisiblity = () => {
    setSinShown(sinShown ? false : true);
  };

  const onSubmit = async (data: any) => {
    //Check if the information matches an existing member; if so, throw an error and ask user to login
    setIsSaving(true);
    if (applicant.id == 0) {
      let memberExists = await client.apiApplicantsSearch(new SearchApplicantModel({
        firstName: data.firstName,
        lastName: data.lastName,
        sin: data.sin ? data.sin : 0,
        dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day)
      })).catch((error) => {
        console.log("There was an error checking for existing member, error");
        setIsSaving(false);
        return;
      });
      if (memberExists) {
        window.scrollTo(0, 0);
        setDisplayExistingMemberError(true);
        setIsSaving(false);
        return;
      }
    }

    dispatch(setTitle(data.title));
    dispatch(setFirstName(data.firstName));
    dispatch(setMiddleName(data.middleName));
    dispatch(setLastName(data.lastName));
    dispatch(setDateOfBirthYear(data.year));
    dispatch(setDateOfBirthMonth(data.month));
    dispatch(setDateOfBirthDay(data.day));
    dispatch(setSin(data.sin));
    dispatch(setPhoneNumber(data.phoneNumber));
    dispatch(setPhoneType(data.phoneType));
    dispatch(setEmailAddress(data.email));
    dispatch(setMaritalStatus(data.maritalStatus));
    dispatch(setNumOfDependents(data.numOfDependents));
    dispatch(setCitizenshipStatus(data.citizenshipStatus));
    dispatch(setFirstTimeBuyer(data.firstTimeBuyer));
    dispatch(setRecentResident(data.newToCanada));

    //Create/update the applicant and create the deal

    if (applicant.id == 0 && !application.isCoApplicant) {
      await client.apiApplicantsCreate(new CreateApplicantModel({
        applicant: new ApplicantForRegistrationDto({
          cbConsent: false,
          caslConsent: credential.credential.meridianCommunication,
          permanentResident: false,
          title: data.title,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
          phoneNumber: data.phoneNumber.replace(/\s/g, "").replaceAll("-", "").replaceAll("(", "").replaceAll(")", ""),
          phoneType: data.phoneType,
          sin: data.sin != null ? data.sin.replace(/\s/g, "") : "",
          email: data.email,
          newToCanada: newToCanada === "yes",
          student: false,
          immigrationResident: false,
          maritalStatus: data.maritalStatus,
          citizenshipStatus: data.citizenshipStatus,
          numberOfDependents: data.numOfDependents,
          alloySessionId: applicant.alloySessionId
        }),
        memberNo: 0,
        startDate: dayjs(new Date()),
        creditScore: 0,
        creditBureauRequestedDate: dayjs(new Date()),
        cbConsent: true,
        isStaff: false,
        title: data.title,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
        phoneNumber: data.phoneNumber.replace(/\s/g, "").replaceAll("-", "").replaceAll("(", "").replaceAll(")", ""),
        phoneType: data.phoneType,
        sin: data.sin != null ? data.sin.replace(/\s/g, "") : "",
        email: data.email,
        newToCanada: newToCanada === "yes",
        student: false,
        immigrationResident: false,
        maritalStatus: data.maritalStatus,
        citizenshipStatus: data.citizenshipStatus,
        numberOfDependents: data.numOfDependents
      })).then(async (result) => {

        if (result != undefined && result.id != undefined) {
          dispatch(setApplicantId(result.id));
        }

        //Call API to Create Deal - Existing Members are not required to disclose Twilio Result
        createDeal(result.id, data.firstTimeBuyer == "yes", applicant.isProspect ? applicant.twilioResult.codeVerified : true, data);

      }).catch((error) => {
        setIsSaving(false);
        if (error.response.code == 2005) {
          window.scrollTo(0, 0);
          setDisplayExistingMemberError(true);
        } else {
          console.log("Error creating applicant", error);
          dispatch(setApplicationProgress(8));
          navigate("/single/co-applicant");
        }
      });
    } else {
      let addresses: AddressDto[] = [];
      addresses.push(new AddressDto({
        id: homeAddress.id,
        streetNumber: homeAddress.streetNumber,
        streetName: homeAddress.streetName,
        streetType: homeAddress.streetType,
        streetDirection: homeAddress.streetDirection,
        unitType: homeAddress.unitType,
        unitNumber: homeAddress.unitNumber,
        city: homeAddress.city,
        region: (homeAddress.province != undefined && homeAddress.province != "") ? homeAddress.province : "N/A",
        postalCode: homeAddress.postalCode,
        country: homeAddress.country,
        timeAtResidenceInMonths: (+applicant.residenceYears * 12) + +applicant.residenceMonths,
        residenceStatusType_Name: applicant.residentStatus,
        deliveryMode: undefined,
        modeNumber: undefined,
        deliveryMode2: undefined,
        modeNumber2: undefined,
        isMailing: false
      }));
      previousAddresses.forEach((prevAddress) => {
        addresses.push(new AddressDto({
          id: prevAddress.id,
          streetNumber: prevAddress.streetNumber,
          streetName: prevAddress.streetName,
          streetType: prevAddress.streetType,
          streetDirection: prevAddress.streetDirection,
          unitType: prevAddress.unitType,
          unitNumber: prevAddress.unitNumber,
          city: prevAddress.city,
          region: (prevAddress.province != undefined && prevAddress.province != "") ? prevAddress.province : "N/A",
          postalCode: prevAddress.postalCode,
          country: prevAddress.country,
          timeAtResidenceInMonths: (prevAddress.yearsLivedOn ? +prevAddress.yearsLivedOn * 12 : 0)
            + (prevAddress.monthsLivedOn ? +prevAddress.monthsLivedOn : 0),
          residenceStatusType_Name: undefined,
          deliveryMode: undefined,
          modeNumber: undefined,
          deliveryMode2: undefined,
          modeNumber2: undefined,
          isMailing: false
        }));
      });

      //Update applicant & employment info (if necessary)
      await client.apiApplicantsPut(new CreateApplicantModel({
        applicant: new ApplicantForRegistrationDto({
          cbConsent: false,
          caslConsent: credential.credential.meridianCommunication,
          permanentResident: false,
          title: data.title,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
          phoneNumber: data.phoneNumber.replace(/\s/g, "").replaceAll("-", "").replaceAll("(", "").replaceAll(")", ""),
          phoneType: data.phoneType,
          sin: data.sin != null && applicant.isProspect ? data.sin.replace(/\s/g, "") : "",
          email: data.email,
          newToCanada: newToCanada === "yes",
          student: false,
          immigrationResident: false,
        }),
        memberNo: 0,
        startDate: dayjs(new Date()),
        creditScore: 0,
        creditBureauRequestedDate: dayjs(new Date()),
        cbConsent: true,
        isStaff: false,
        isPrimary: true,
        title: data.title,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
        phoneNumber: data.phoneNumber.replace(/\s/g, "").replaceAll("-", "").replaceAll("(", "").replaceAll(")", ""),
        phoneType: data.phoneType,
        sin: data.sin != null && applicant.isProspect ? data.sin.replace(/\s/g, "") : "",
        email: data.email,
        newToCanada: newToCanada === "yes",
        student: false,
        immigrationResident: false,
        maritalStatus: data.maritalStatus,
        citizenshipStatus: data.citizenshipStatus,
        numberOfDependents: data.numOfDependents,
        address: addresses,
      })).then(async (result) => {
        console.log(result);

        if (application.id == undefined || application.id == 0) {
          //Call API to Create Deal (for Existing Members - Twilio Result is not required)
          createDeal(result.id, data.firstTimeBuyer == "yes", applicant.isProspect ? applicant.twilioResult.codeVerified : true, data);
        } else {

          updateDeal(application.id, data.firstTimeBuyer == "yes", data);
        }

      }).catch((error) => {
        setIsSaving(false);
        console.log("Error updating applicant", error);
        dispatch(setApplicationProgress(8));
        navigate("/single/co-applicant");
      });
    }
  };

  const createDeal = async (applicantId: number | undefined, isFirstTimeBuyer: boolean, twilioPhoneResult: boolean, formData: any) => {
    if (referralProducts != undefined && referralProducts.length > 0) {
    }
    if (applicantId != undefined) {
      await client.apiDealsPost(new CreateDealCommand({
        applicantId: applicantId,
        mortgageApplication: new MortgageApplicationForCreateDto({
          mortgageApplicationType: application.applicationCategory == applicationCategory.Buy ? application.searchingForProperty ? "preapproval" : "approval" : "",
          productCode: "",
          purposeType: application.applicationCategory == applicationCategory.Buy ? "Purchase"
            : application.applicationCategory == applicationCategory.Refinance ? "Refinance" : "Transfer",
          securityType: "Unsecured",
          firstTimeHomeBuyer: isFirstTimeBuyer,
          termInMonths: application.termLength != "unknown" ? +application.termLength * 12 : 0,
          amortizationInMonths: application.amortizationLength != "unknown" ? +application.amortizationLength * 12 : 0,
          remainingBalanceAmount: propertyInfo.mortgageBalance,
          downPaymentAmount: application.downPayment,
          newMoneyRequestedAmount: 0.0,
          totalLoanAmount: application.applicationCategory == applicationCategory.Buy ? propertyInfo.purchasePrice - application.downPayment : application.totalLoan,
          purchasePrice: application.applicationCategory == applicationCategory.Buy ? propertyInfo.purchasePrice : propertyInfo.propertyValue,
          propertySalePrice: 0
        }),
        twilioVerified: twilioPhoneResult,
        alloySessionId: applicant.alloySessionId,
        referralProduct: referralProducts.length > 0 ? new ReferralProductDto({
          description: referralProducts[0].description,
          rate: referralProducts[0].loanConfig?.productRate ? referralProducts[0].loanConfig?.productRate.toString() + "%" : "",
          promoRate: referralProducts[0].loanConfig?.promoRate ? referralProducts[0].loanConfig?.promoRate.toString() + "%" : "",
          productId: referralProducts[0].productID ? +referralProducts[0].productID : 0,
        }) : undefined
      })).then(async (result) => {

        //Add Employment Info
        //await updateEmployment(applicantId, result.id ? result.id : 0, formData);

        if (result != undefined && result.id != undefined) {
          dispatch(setApplicationId(result.id));

          //If an existing member has assets in ovation, fetch and store them in redux
          let displayId = 1;
          if (result.assets && result.assets.nonRegistered) {
            result.assets.nonRegistered.forEach(asset => {
              let newAsset: assetInfo = {
                displayId: displayId.toString(),
                id: asset.id ? asset.id : 0,
                visible: true,
                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                origin: asset.origin_Name ? asset.origin_Name : "",
                value: asset.value ? asset.value : 0,
                valueText: asset.value ? asset.value.toString() : "",
                description: asset.description ? asset.description : "",
                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                downPaymentValueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                applicants: asset.applicants ? asset.applicants : [],
                details: {
                  nonRegistered: {
                    financialInstitution: asset.fiName ? asset.fiName : "",
                    otherFinancialInstitution: asset.fiNameOther ? asset.fiNameOther : "",
                  },
                  registered: undefined,
                  realEstate: undefined,
                  vehicle: undefined,
                  lifeInsurance: undefined
                }
              }
              dispatch(addAsset(newAsset));
              displayId++;
            });
          }
          if (result.assets && result.assets.registered) {
            result.assets.registered.forEach(asset => {
              let newAsset: assetInfo = {
                displayId: displayId.toString(),
                id: asset.id ? asset.id : 0,
                visible: true,
                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                origin: asset.origin_Name ? asset.origin_Name : "",
                value: asset.value ? asset.value : 0,
                valueText: asset.value ? asset.value.toString() : "",
                description: asset.description ? asset.description : "",
                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                downPaymentValueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                applicants: asset.applicants ? asset.applicants : [],
                details: {
                  nonRegistered: undefined,
                  registered: {
                    financialInstitution: asset.fiName ? asset.fiName : "",
                    otherFinancialInstitution: asset.fiNameOther ? asset.fiNameOther : "",
                  },
                  realEstate: undefined,
                  vehicle: undefined,
                  lifeInsurance: undefined
                }
              }
              dispatch(addAsset(newAsset));
              displayId++;
            });
          }
          if (result.assets && result.assets.realEstate) {
            result.assets.realEstate.forEach(asset => {
              let originalPurchaseDateString = "";
              let soldDate = "";
              if (asset.origPurchasePrice) {
                let origPurchaseDateJs = asset.origPurchaseDate ? asset.origPurchaseDate : dayjs();
                let origPurchaseMonth = (origPurchaseDateJs.month() + 1).toString();
                originalPurchaseDateString =
                  `${origPurchaseDateJs.year()}-${origPurchaseMonth.length > 1 ? origPurchaseMonth : "0" + origPurchaseMonth}-` +
                  `${origPurchaseDateJs.date().toString().length > 1 ? origPurchaseDateJs.date() : "0" + origPurchaseDateJs.date()}`
              }
              if (asset.soldDate) {
                let soldDateJs = asset.soldDate;
                let soldMonth = (soldDateJs.month() + 1).toString();
                soldDate =
                  `${soldDateJs.year()}-${soldMonth.length > 1 ? soldMonth : "0" + soldMonth}-` +
                  `${soldDateJs.date().toString().length > 1 ? soldDateJs.date() : "0" + soldDateJs.date()}`
              }
              let newAsset: assetInfo = {
                displayId: displayId.toString(),
                id: asset.id ? asset.id : 0,
                visible: true,
                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                origin: asset.origin_Name ? asset.origin_Name : "",
                value: asset.value ? asset.value : 0,
                valueText: asset.value ? asset.value.toString() : "",
                description: asset.description ? asset.description : "",
                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                downPaymentValueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                applicants: asset.applicants ? asset.applicants : [],
                details: {
                  nonRegistered: undefined,
                  registered: undefined,
                  realEstate: {
                    occupancyType: asset.occupancyType_Name ? asset.occupancyType_Name : "",
                    propertyType: asset.propertyType_Name ? asset.propertyType_Name : "",
                    rentalComponent: asset.rentalComponent ? "yes" : "no",
                    numOfProperties: asset.numberOfProperties ? asset.numberOfProperties.toString() : "",
                    address: {
                      displayId: "1",
                      streetNumber: asset.assetAddress_StreetNumber ? asset.assetAddress_StreetNumber : "",
                      streetName: asset.assetAddress_StreetName ? asset.assetAddress_StreetName : "",
                      streetType: asset.assetAddress_StreetType ? asset.assetAddress_StreetType : "",
                      streetDirection: asset.assetAddress_StreetDirection ? asset.assetAddress_StreetDirection : "",
                      unitType: asset.assetAddress_UnitType ? asset.assetAddress_UnitType : "",
                      unitNumber: asset.assetAddress_UnitNumber ? asset.assetAddress_UnitNumber : "",
                      city: asset.assetAddress_City ? asset.assetAddress_City : "",
                      province: asset.assetAddress_Region ? asset.assetAddress_Region : "",
                      postalCode: asset.assetAddress_PostalCode ? asset.assetAddress_PostalCode : "",
                      country: asset.assetAddress_Country ? asset.assetAddress_Country : "",
                    },
                    originalPurchasePrice: asset.origPurchasePrice ? asset.origPurchasePrice : 0,
                    originalPurchasePriceText: asset.origPurchasePrice ? asset.origPurchasePrice.toString() : "",
                    originalPurchaseDate: originalPurchaseDateString,
                    propertyTax: 0,
                    propertyTaxText: "",
                    heatingCost: 0,
                    heatingCostText: "",
                    maintenanceFee: 0,
                    maintenanceFeeText: "",
                    ownershipPercentage: "",
                    lot: asset.lot ? asset.lot : "",
                    block: asset.block ? asset.block : "",
                    plan: asset.plan ? asset.plan : "",
                    section: asset.section ? asset.section : "",
                    futureUsage: asset.futureUsageType_Name ? asset.futureUsageType_Name : "",
                    hasPropertyBeenSold: asset.hasPropertyBeenSold ? "yes" : "no",
                    soldDate: soldDate,
                    rentalIncome: asset.rentalIncome ? asset.rentalIncome : 0,
                    rentalIncomeText: asset.rentalIncome ? asset.rentalIncome.toString() : "",
                    manualAddressEntered: false,
                    isCurrentResidence: false
                  },
                  vehicle: undefined,
                  lifeInsurance: undefined
                }
              }
              dispatch(addAsset(newAsset));
              displayId++;
            });
          }
          if (result.assets && result.assets.vehicle) {
            result.assets.vehicle.forEach(asset => {
              let newAsset: assetInfo = {
                displayId: displayId.toString(),
                id: asset.id ? asset.id : 0,
                visible: true,
                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                origin: asset.origin_Name ? asset.origin_Name : "",
                value: asset.value ? asset.value : 0,
                valueText: asset.value ? asset.value.toString() : "",
                description: asset.description ? asset.description : "",
                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                downPaymentValueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                applicants: asset.applicants ? asset.applicants : [],
                details: {
                  nonRegistered: undefined,
                  registered: undefined,
                  realEstate: undefined,
                  vehicle: {
                    make: asset.make ? asset.make : "",
                    model: asset.model ? asset.model : "",
                    year: asset.year ? asset.year.toString() : "",
                    ownership: asset.vehicleOwnershipType_Name ? asset.vehicleOwnershipType_Name : "",
                  },
                  lifeInsurance: undefined
                }
              };
              dispatch(addAsset(newAsset));
              displayId++;
            });
          }
          if (result.assets && result.assets.lifeInsurance) {
            result.assets.lifeInsurance.forEach(asset => {
              let newAsset: assetInfo = {
                displayId: displayId.toString(),
                id: asset.id ? asset.id : 0,
                visible: true,
                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                origin: asset.origin_Name ? asset.origin_Name : "",
                value: asset.value ? asset.value : 0,
                valueText: asset.value ? asset.value.toString() : "",
                description: asset.description ? asset.description : "",
                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                downPaymentValueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                applicants: asset.applicants ? asset.applicants : [],
                details: {
                  nonRegistered: undefined,
                  registered: undefined,
                  realEstate: undefined,
                  vehicle: undefined,
                  lifeInsurance: {
                    cashSurrenderValue: asset.cashSurrenderValue ? asset.cashSurrenderValue : 0,
                    cashSurrenderValueText: asset.cashSurrenderValue ? asset.cashSurrenderValue.toString() : "",
                  }
                }
              };
              dispatch(addAsset(newAsset));
              displayId++;
            });
          }
          if (result.assets && result.assets.other) {
            result.assets.other.forEach(asset => {
              let newAsset: assetInfo = {
                displayId: displayId.toString(),
                id: asset.id ? asset.id : 0,
                visible: true,
                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                origin: asset.origin_Name ? asset.origin_Name : "",
                value: asset.value ? asset.value : 0,
                valueText: asset.value ? asset.value.toString() : "",
                description: asset.description ? asset.description : "",
                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                downPaymentValueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                applicants: asset.applicants ? asset.applicants : [],
                details: {
                  nonRegistered: undefined,
                  registered: undefined,
                  realEstate: undefined,
                  vehicle: undefined,
                  lifeInsurance: undefined
                }
              };
              dispatch(addAsset(newAsset));
              displayId++;
            });
          }
          if (result.assets && result.assets.downPayment) {
            result.assets.downPayment.forEach(asset => {
              let newAsset: assetInfo = {
                displayId: displayId.toString(),
                id: asset.id ? asset.id : 0,
                visible: true,
                assetCategory: asset.assetType_Name ? asset.assetType_Name : "",
                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                origin: asset.origin_Name ? asset.origin_Name : "",
                value: asset.downPaymentValue ? asset.downPaymentValue : 0,
                valueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                description: asset.description ? asset.description : "",
                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                downPaymentValueText: asset.downPaymentValue ? asset.downPaymentValue.toString() : "",
                applicants: asset.applicants ? asset.applicants : [],
                details: {
                  nonRegistered: undefined,
                  registered: undefined,
                  realEstate: undefined,
                  vehicle: undefined,
                  lifeInsurance: undefined
                }
              };
              dispatch(addAsset(newAsset));
              displayId++;
            });
          }
        }
        setIsSaving(false);
        dispatch(setApplicationProgress(8));
        setClickInsightApplicationProgress('Build your profile - Tell us about yourself');
        navigate("/single/co-applicant");

      }).catch((error) => {
        setIsSaving(false);
        console.log("Error creating deal", error);
        dispatch(setApplicationProgress(8));
        navigate("/single/co-applicant");
      });

    } else {
      dispatch(setApplicationProgress(8));
      navigate("/single/co-applicant");
    }
  }

  const updateDeal = async (applicantId: number | undefined, isFirstTimeBuyer: boolean, formData: any) => {
    if (applicantId != undefined) {
      await client.apiDealsPut(new UpdateDealCommand({
        firstTimeHomeBuyer: isFirstTimeBuyer,
        relationship: "Owner",
        primary: true
      })).then(async (result) => {
        await updateEmploymentInfo(applicant.id, application.id, formData);
      }).catch(async (error) => {
        console.log("Error updating deal", error);
        await updateEmploymentInfo(applicant.id, application.id, formData);
      });
    } else {
      await updateEmploymentInfo(applicant.id, application.id, formData);
    }
  }

  const updateEmploymentInfo = async (applicantId: number, dealId: number, formData: any) => {
    //Update Employment Info
    //await updateEmployment(applicantId, dealId, formData);
    dispatch(setApplicationProgress(8));
    setIsSaving(false);
    navigate("/single/co-applicant");
  }

  const updateEmployment = async (applicantId: number, dealId: number, formData: any) => {
    if (employment.id == 0) {
      await client.apiEmploymentsPost(new CreateEmploymentModel({
        applicantId: applicantId,
        dealId: dealId,
        employment: [new EmploymentDto({
          employmentType_Name: formData.employmentStatus,
          employmentPositionType_Name: formData.employmentStatus == "retired" ? "" : formData.employmentType,
          employer_Occupation_Name: formData.employmentStatus == "retired" ? "retired" : formData.employmentStatus == "unemployed" ? "Unemployed" : "",
          fulltime: methods.getValues("employmentType") == "fulltime",
          employmentIncomes: formData.employmentStatus == "unemployed" || formData.employmentStatus == "retired" ? [] : [new EmploymentIncomeDto({
            id: 0,
            applicantId: applicantId,
            dealId: dealId,
            incomeCategoryType_Name: "employment",
            incomeType_Name: formData.employmentStatus == "retired" ? "salary" : formData.payrollType,
            currencyType_Name: "cad",
            frequencyType_Name: formData.payrollFrequency,
            amountPerFrequency: +incomeAmount,
            primaryIncomeSource: true
          })]
        })],
        otherIncome: formData.employmentStatus == "employed" || formData.employmentStatus == "selfemployed" ? [] : [new OtherIncome({
          applicantId: applicantId,
          dealId: dealId,
          incomeCategoryType_Name: "other",
          incomeType_Name: formData.employmentStatus == "retired" ? "employerpension" : "other",
          description: formData.employmentStatus == "unemployed" ? "Unemployed" : undefined,
          currencyType_Name: "cad",
          frequencyType_Name: formData.payrollFrequency,
          amountPerFrequency: +incomeAmount,
          primaryIncomeSource: true,
          twoPlusYears: undefined
        })]
      })).then((result) => {
        if (result != undefined && result.length > 0) {
          let currentEmployment = result[0];
          if (currentEmployment && currentEmployment.id) {
            dispatch(setEmploymentId(currentEmployment.id));
          }
        }

      }).catch((error) => {
        setIsSaving(false);
        console.log("Error saving employment details", error);
      });
    } else {
      await client.apiEmploymentsPut(new UpdateEmploymentModel({
        applicantId: applicantId,
        dealId: dealId,
        employment: new EmploymentForManipulationDto({
          id: employment.id,
          employmentType_Name: formData.employmentStatus,
          employmentPositionType_Name: formData.employmentStatus == "retired" ? "" : formData.employmentType,
          employer_Occupation_Name: formData.employmentStatus == "retired" ? "retired" : formData.employmentStatus == "unemployed" ? "Unemployed" : "",
          fulltime: methods.getValues("employmentType") == "fulltime",
          employmentIncomes: formData.employmentStatus == "unemployed" || formData.employmentStatus == "retired" ? [] : [new EmploymentIncomeDto({
            id: 0,
            applicantId: applicantId,
            dealId: dealId,
            incomeCategoryType_Name: "employment",
            incomeType_Name: formData.employmentStatus == "retired" ? "salary" : formData.payrollType,
            currencyType_Name: "cad",
            frequencyType_Name: formData.payrollFrequency,
            amountPerFrequency: +incomeAmount,
            primaryIncomeSource: true,
            employmentId: employment.id
          })]
        }),
        otherIncome: formData.employmentStatus == "employed" || formData.employmentStatus == "selfemployed" ? [] : [new OtherIncome({
          applicantId: applicantId,
          dealId: dealId,
          incomeCategoryType_Name: "other",
          incomeType_Name: formData.employmentStatus == "retired" ? "employerpension" : "other",
          description: formData.employmentStatus == "unemployed" ? "Unemployed" : undefined,
          currencyType_Name: "cad",
          frequencyType_Name: formData.payrollFrequency,
          amountPerFrequency: +incomeAmount,
          primaryIncomeSource: true,
          twoPlusYears: undefined
        })]
      })).then((result) => {

      }).catch((error) => {
        setIsSaving(false);
        console.log("Error saving employment details", error);
      });
    }
  }

  return (
    <SlideTransition>
      <main aria-labelledby="main-content" className="main-content">
        <div className="flex flex-col max-xs:px-3 sm:ml-72 max-md:items-center max-md:mt-20" id="main">
          <div className={style.piHeading}>
            <div>
              <h1>Tell us about yourself</h1>
              <p className="sub-heading">
                Make sure to enter your information as it appears on your Canadian
                government-issued ID. We may need to verify your identity later
                using your Canadian government-issued ID, so it has to match.
              </p>
            </div>
            <i className="text-sm mt-5">
              All information is required unless marked optional.
            </i>
            {displayExistingMemberError &&
              <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                <img src="assets/error-info-icon.svg" alt="error" />
                <span className="text-sm ml-2 mt-1">
                  Looks like you’re already a Meridian member. To start an application please log in using your Meridian online banking credentials.
                  <span className="underline cursor-pointer ml-5" onClick={() => {
                    dispatch(clearApplicationState());
                    dispatch(clearApplicantState());
                    navigate("/login", { replace: true })
                  }} style={{ color: "var(--error-red)" }}>Log in</span>
                </span>
              </div>
            }
            <div className="flex flex-row mt-5">
              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                  <div className="col-span-1 mb-3">
                    <label htmlFor="title">Title (optional)</label>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.title ? "" : "hidden",
                            "w-4 h-4 absolute top-5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <select
                        id="title"
                        {...methods.register("title", {})}
                        defaultValue=""
                        className={clsx(
                          methods.formState?.errors?.title?.message ? "error" : "",
                          style.frequency, "mt-2 pt-2.5"
                        )}
                      >
                        <option hidden value="">
                          Choose...
                        </option>
                        <option value="mr">Mr</option>
                        <option value="mrs">Mrs</option>
                        <option value="ms">Ms</option>
                        <option value="miss">Miss</option>
                        <option value="dr">Dr</option>
                        <option value="rev">Rev</option>
                      </select>
                    </div>
                  </div>
                  {methods.formState?.errors?.title?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.title.message}
                    </span>
                  )}
                  <div className="col-span-1 mt-5">
                    <label htmlFor="firstName">Legal first name</label>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.firstName ? "" : "hidden",
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.firstName?.message
                            ? "error"
                            : "",
                          "mt-2"
                        )}
                        disabled={!applicant.isProspect && applicant.existingMemberFields.isFirstNameAvailable}
                        type="text"
                        id="firstName"
                        {...methods.register("firstName", {
                          required: "Enter your first name",
                          minLength: {
                            value: 2,
                            message: "Name must be at least 2 characters",
                          },
                          maxLength: {
                            value: 38,
                            message: "Name exceeds maximum character count",
                          },
                          pattern: {
                            value: /^[a-zA-Z0-9 '-.]*$/,
                            message: "Please enter valid first name",
                          },
                          onBlur: () => {
                            methods.setValue("firstName", methods.getValues("firstName").trim());
                          },
                        })}
                      />
                    </div>
                    {methods.formState?.errors?.firstName?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.firstName.message}
                      </span>
                    )}

                    <div className="mt-5">
                      <label htmlFor="middleName">Middle Name (optional)</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.middleName ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.middleName?.message
                              ? "error"
                              : "",
                            "mt-2"
                          )}
                          type="text"
                          id="middleName"
                          disabled={!applicant.isProspect && applicant.existingMemberFields.isLastNameAvailable}
                          {...methods.register("middleName", {
                            maxLength: {
                              value: 15,
                              message: "Name exceeds maximum character count",
                            },
                            pattern: {
                              value: /^[a-zA-Z0-9 '-.]*$/,
                              message: "Please enter valid middle name",
                            },
                            onBlur: () => {
                              methods.setValue("middleName", methods.getValues("middleName").trim());
                            },
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.middleName?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.middleName.message}
                        </span>
                      )}
                    </div>

                    <div className="mt-5">
                      <label htmlFor="lastName">Legal last name</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.lastName ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.lastName?.message
                              ? "error"
                              : "",
                            "mt-2"
                          )}
                          type="text"
                          id="lastName"
                          disabled={!applicant.isProspect}
                          {...methods.register("lastName", {
                            required: "Enter your last name",
                            minLength: {
                              value: 2,
                              message: "Name must be at least 2 characters",
                            },
                            maxLength: {
                              value: 38,
                              message: "Name exceeds maximum character count",
                            },
                            pattern: {
                              value: /^[a-zA-Z0-9 '-.]*$/,
                              message: "Please enter valid last name",
                            },
                            onBlur: () => {
                              methods.setValue("lastName", methods.getValues("lastName").trim());
                            },
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.lastName?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.lastName.message}
                        </span>
                      )}
                    </div>
                    <div className="mt-5">
                      <label htmlFor="dobMonth">Date of birth
                        <DateComponent readOnly={!applicant.isProspect} callbackYear={setYearHandler} callbackMonth={setMonthHandler} callbackDay={setDayHandler} validateDay={validateDay} validateYear={validateYear} />
                      </label>
                    </div>
                    <div className="col-span-1">
                      <div
                        className={clsx(
                          "flex mt-5",
                          style.Sin
                        )}
                      >
                        <span className="mr-1"> Social Insurance Number (optional)</span>
                        <span className="ml-2">
                          <ToolTip
                            title="Where can I find my SIN?"
                            content="Providing your SIN helps us retrieve the most accurate credit information. If you can't remember your SIN, you can find it on your income tax return, tax slips, record of employment and RRSP contribution form."
                            classNames={style.sinToolTip}
                          />
                        </span>
                      </div>
                      <div className="flex relative">
                        <span className={clsx(sinShown ? "hidden" : " ")}>
                          <img
                            onClick={toggleSinVisiblity}
                            src="assets/eye-icon.svg"
                            className={clsx(
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <span className={clsx(sinShown ? " " : "hidden")}>
                          <img
                            onClick={toggleSinVisiblity}
                            src="assets/eye-slash-icon.svg"
                            className={clsx(
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.sin ? "error" : "",
                            "mt-2"
                          )}
                          type={sinShown ? "text" : "password"}
                          autoComplete="new-password"
                          {...methods.register("sin", {
                            validate: {
                              sinValidator: (v) => {
                                if (v == "" || v == undefined) {
                                  return true;
                                } else {
                                  return isValidSin(v) == true;
                                }

                              },
                            }
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.sin?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.sin.message}
                        </span>
                      )}
                      {methods.formState?.errors?.sin?.type == "sinValidator" && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          Enter a valid SIN
                        </span>
                      )}
                    </div>
                    <div className="mt-5">
                      <label htmlFor="email">Email Address</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.email ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            methods.formState?.errors?.email?.message
                              ? "error"
                              : "",
                            "mt-2"
                          )}
                          type="text"
                          id="email"
                          disabled={applicant.isProspect}
                          {...methods.register("email", {
                            required: "Enter your email address",
                            pattern: {
                              value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                              message: "Email address must be a valid address",
                            },
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.email?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.email.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex md:flex-row flex-col w-max mb-5">
                      <div className="mt-5">
                        <label htmlFor="phoneNumber">Phone number</label>
                        <div className="flex relative">
                          <span>
                            <img
                              src="assets/error-icon.svg"
                              className={clsx(
                                methods.formState?.errors?.phoneNumber
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <span>
                            <img
                              src="assets/success-icon.svg"
                              className={clsx(
                                applicant.isProspect
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <InputMask
                            type="tel"
                            placeholder="(000) 000-0000"
                            alwaysShowMask={false}
                            maskChar={null}
                            mask="(999) 999-9999"
                            className={clsx(
                              methods.formState?.errors?.phoneNumber?.message
                                ? "error"
                                : "",
                              "mt-2 w:[200px] md:w-[305px]"
                            )}
                            disabled={applicant.isProspect}
                            {...methods.register("phoneNumber", {
                              required: "Enter a valid phone number",
                              pattern: {
                                value:
                                  // eslint-disable-next-line no-useless-escape
                                  /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                                message: "Enter a valid phone number",
                              },
                            })}
                          />
                        </div>
                        {methods.formState?.errors?.phoneNumber?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            Enter a valid phone number
                          </span>
                        )}
                      </div>
                      <div className="md:ml-4 mt-5">
                        <label htmlFor="phoneType">Phone type</label>
                        <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md mx-auto">
                          <li className="relative">
                            <input
                              {...methods.register("phoneType", {
                                required: applicant.isProspect ? "Select a phone type" : undefined
                              })}
                              type="radio"
                              value="Mobile"
                              id="mobile"
                              className="sr-only peer "
                            />
                            <label
                              className={clsx(
                                style.phoneLabel,
                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                methods.formState?.errors?.phoneType?.message
                                  ? style.labelError
                                  : ""
                              )}
                              htmlFor="mobile"
                            >
                              <p className="p-2 brand-blue-text leading-6">Mobile</p>
                            </label>
                          </li>
                          <li className="relative">
                            <input
                              {...methods.register("phoneType", {
                                required: applicant.isProspect ? "Select a phone type" : undefined
                              })}
                              type="radio"
                              value="Home"
                              id="home"
                              className="sr-only peer leading-6"
                            />
                            <label
                              className={clsx(
                                style.phoneLabel,
                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                methods.formState?.errors?.phoneType?.message
                                  ? style.labelError
                                  : ""
                              )}
                              htmlFor="home"
                            >
                              <p className="p-2 brand-blue-text leading-6">Home</p>
                            </label>
                          </li>
                        </ul>
                        {methods.formState?.errors?.phoneType?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            {methods.formState?.errors?.phoneType?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="col-span-1 mb-5">
                      <label htmlFor="maritalStatus">Marital Status</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.maritalStatus ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <select
                          id="maritalStatus"
                          {...methods.register("maritalStatus", {
                            required: "Select an option",
                          })}
                          defaultValue=""
                          style={{ maxWidth: 'none' }}
                          className={clsx(
                            methods.formState?.errors?.maritalStatus?.message ? "error" : "",
                            "mt-2 pt-2.5"
                          )}
                        >
                          <option hidden value="">
                            Choose...
                          </option>
                          <option value="single">Single</option>
                          <option value="married">Married</option>
                          <option value="separated">Separated</option>
                          <option value="divorced">Divorced</option>
                          <option value="common_law">Common-Law</option>
                          <option value="widowed">Widowed</option>
                        </select>
                      </div>
                      {methods.formState?.errors?.maritalStatus?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.maritalStatus.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div
                      className={clsx(
                        "flex mt-5",
                        style.Sin
                      )}
                    >
                      <span className="mr-1"> Number of Dependents</span>
                      <span className="ml-2">
                        <ToolTip
                          title="Why are we asking this?"
                          content="We would like to help you identify other sources of income throughout the application, and learning more about you will help us do this."
                          classNames={style.sinToolTip}
                        />
                      </span>
                    </div>
                    <div className="flex relative mb-3">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.numOfDependents ? "" : "hidden",
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.numOfDependents?.message
                            ? "error"
                            : "",
                          "mt-2"
                        )}
                        {...methods.register("numOfDependents", {
                          required: "Enter the number of dependents in your household.",
                          validate: {
                            numOfDependents: (c) => {
                              if (+c >= 0 && +c <= 20) {
                                return true
                              } else {
                                return "Number of dependents must be between 0 and 20"
                              }
                            },
                          },
                        })}
                        type="number"
                        id="dependentsCount"
                        max={20}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    {methods.formState?.errors?.numOfDependents?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.numOfDependents.message}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="col-span-1 mb-5 mt-3">
                      <label htmlFor="citizenshipStatus">Citizenship Status</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.citizenshipStatus ? "" : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <select
                          id="citizenshipStatus"
                          {...methods.register("citizenshipStatus", {
                            required: "Select an option",
                          })}
                          style={{ maxWidth: 'none' }}
                          defaultValue=""
                          className={clsx(
                            methods.formState?.errors?.citizenshipStatus?.message ? "error" : "",
                            "mt-2 pt-2.5"
                          )}
                        >
                          <option hidden value="">
                            Choose...
                          </option>
                          <option value="citizen">Citizen</option>
                          <option value="resident">Permanent Resident</option>
                          <option value="workvisa">Work Visa</option>
                          <option value="studentvisa">Student Visa</option>
                          <option value="nonresident">Non-Resident</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      {methods.formState?.errors?.citizenshipStatus?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.citizenshipStatus.message}
                        </span>
                      )}
                    </div>
                  </div>
                  {application.applicationCategory == applicationCategory.Buy &&
                    <div>
                      <div className="grid grid-flow-row sm:grid-flow-col">
                        <label className="mb-2">Are you a first-time home buyer?</label>
                      </div>
                      <div className="grid grid-flow-row sm:grid-flow-col">
                        <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                          <li className="relative">
                            <input
                              type="radio"
                              value="yes"
                              id="yes-first-time-buyer"
                              {...methods.register("firstTimeBuyer", {
                                required: 'Select an option',
                                onChange: firstTimeBuyerChangeHandler,
                              })}
                              checked={firstTimeHomeBuyer === "yes"}
                              onChange={firstTimeBuyerChangeHandler}
                              className="sr-only peer "
                            />
                            <label
                              className={clsx(
                                style.phoneLabel,
                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                methods.formState?.errors?.firstTimeBuyer?.message
                                  ? style.labelError
                                  : ""
                              )}
                              htmlFor="yes-first-time-buyer"
                            >
                              <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                            </label>
                          </li>
                          <li className="relative">
                            <input
                              type="radio"
                              value="no"
                              id="no-first-time-buyer"
                              {...methods.register("firstTimeBuyer", {
                                required: 'Select an option',
                                onChange: firstTimeBuyerChangeHandler,
                              })}
                              checked={firstTimeHomeBuyer === "no"}
                              onChange={firstTimeBuyerChangeHandler}
                              className="sr-only peer leading-6"
                            />
                            <label
                              className={clsx(
                                style.phoneLabel,
                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                methods.formState?.errors?.firstTimeBuyer?.message
                                  ? style.labelError
                                  : ""
                              )}
                              htmlFor="no-first-time-buyer"
                            >
                              <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                            </label>
                          </li>
                        </ul>
                      </div>
                      {methods.formState?.errors?.firstTimeBuyer?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.firstTimeBuyer.message}
                        </span>
                      )}
                      <p><a className="active" style={{ textDecoration: "underline" }} href="https://www.meridiancu.ca/good-sense/posts/what-s-new-for-first-time-home-buyers" target="_blank" >Learn more</a> about being a first-time home buyer.</p>
                    </div>
                  }
                  <div>
                    <div className="grid grid-flow-row sm:grid-flow-col mt-5">
                      <label className="mb-2">Did you arrive in Canada in the last 5 years?</label>
                    </div>
                    <div className="grid grid-flow-row sm:grid-flow-col">
                      <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                        <li className="relative">
                          <input
                            type="radio"
                            value="yes"
                            id="yes-new-resident"
                            {...methods.register("newToCanada", {
                              required: 'Select an option',
                              onChange: firstTimeBuyerChangeHandler,
                            })}
                            checked={newToCanada === "yes"}
                            onChange={recentResidentChangeHandler}
                            className="sr-only peer "
                          />
                          <label
                            className={clsx(
                              style.phoneLabel,
                              "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                              methods.formState?.errors?.newToCanada?.message
                                ? style.labelError
                                : ""
                            )}
                            htmlFor="yes-new-resident"
                          >
                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                          </label>
                        </li>
                        <li className="relative">
                          <input
                            type="radio"
                            value="no"
                            id="no-new-resident"
                            {...methods.register("newToCanada", {
                              required: 'Select an option',
                              onChange: firstTimeBuyerChangeHandler,
                            })}
                            checked={newToCanada === "no"}
                            onChange={recentResidentChangeHandler}
                            className="sr-only peer leading-6"
                          />
                          <label
                            className={clsx(
                              style.phoneLabel,
                              "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                              methods.formState?.errors?.newToCanada?.message
                                ? style.labelError
                                : ""
                            )}
                            htmlFor="no-new-resident"
                          >
                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                          </label>
                        </li>
                      </ul>
                    </div>
                    {methods.formState?.errors?.newToCanada?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.newToCanada.message}
                      </span>
                    )}
                  </div>

                  <div className="flex max-sm:hidden">
                    <div className="mb-5 mr-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-169 sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                  </div>
                  <div className="sm:hidden">
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                    <div className="mb-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-full sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </main>
    </SlideTransition>
  );
}